import { AssumedInjuryV2, Report } from 'store/ducks'

/**
 * Determines whether to use old legacy injury format or new injuryDataV2 format
 * Based on the same logic used in report-fill-validations.ts
 */
export const shouldUseOldInjuryFormat = (report: Report): boolean => {
  const { injury } = report

  // Check if legacy injury data exists
  const hasLegacyInjuryData =
    injury &&
    ((injury.assumedInjury &&
      Object.values(injury.assumedInjury).some(Boolean)) ||
      (injury.injuredBodyPart &&
        Object.values(injury.injuredBodyPart).some(Boolean)))

  // Determine if report is after September 1, 2025 (new format cutoff date)
  const reportDate = new Date(
    report.reportDate || report.createdAt || new Date()
  )
  const cutoffDate = new Date('2025-09-01T00:00:00.000Z')
  const isAfterCutoffDate = reportDate >= cutoffDate

  // Use old validation if there's legacy data and no new format, OR if report is before cutoff date and no injury data exists
  return (
    (hasLegacyInjuryData && !report.injuryDataV2) ||
    (!isAfterCutoffDate && !hasLegacyInjuryData && !report.injuryDataV2)
  )
}

/**
 * Removes empty injury entries from injuryDataV2.assumedInjuries array
 * An entry is considered empty if all fields (injuredBodyPart, assumedInjury, comments) are null/undefined/empty
 * Returns a new report object to maintain Redux immutability
 */
export const cleanupEmptyInjuryEntries = (report: Report): Report => {
  if (report?.injuryDataV2?.assumedInjuries) {
    const filteredInjuries = report.injuryDataV2.assumedInjuries.filter(
      injury => injuryEntryHasData(injury)
    )

    // Return a new report object with filtered injuries (immutable update)
    return {
      ...report,
      injuryDataV2: {
        ...report.injuryDataV2,
        assumedInjuries: filteredInjuries
      }
    }
  }
  return report
}

export const injuryEntryHasData = (injury: AssumedInjuryV2): boolean => {
  if (!injury) return false
  const hasBodyPart =
    !!injury.injuredBodyPart && `${injury.injuredBodyPart}`.trim() !== ''
  const hasInjuryType =
    !!injury.assumedInjury && `${injury.assumedInjury}`.trim() !== ''
  const hasComments = !!injury.comments && injury.comments.trim() !== ''

  return hasBodyPart || hasInjuryType || hasComments
}
